import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE } from "../lib/api";
import { Thumbnail } from "../components/Thumbnail";
import { getItemThumbnailUrl, sanitizeThumbnailUrl } from "../lib/thumbnailUrl";

const ADMIN_KEY = import.meta.env.VITE_ADMIN_KEY;

interface LibraryItem {
  id: string;
  title: string;
  created_at: string;
  duration_ms?: number;
  transcript_json?: string;
  video_id?: string;
  channel_name?: string;
  user_email?: string;
  user_id?: string;
  word_count?: number;
  source?: string;
  thumbnail_url?: string;
}

interface Segment {
  end_ms?: number;
  text?: string;
}

function computeDurationMs(item: LibraryItem): number {
  if (item.duration_ms && Number.isFinite(item.duration_ms) && item.duration_ms > 0) {
    return item.duration_ms;
  }
  if (item.transcript_json) {
    try {
      const segs: Segment[] = JSON.parse(item.transcript_json);
      const last = segs[segs.length - 1];
      if (last?.end_ms) return last.end_ms;
    } catch { /* ignore */ }
  }
  return 0;
}

function computeWordCount(item: LibraryItem): number {
  if (item.word_count && item.word_count > 0) return item.word_count;
  if (item.transcript_json) {
    try {
      const segs: Segment[] = JSON.parse(item.transcript_json);
      return segs.reduce((acc, s) => acc + (s.text?.split(" ").length || 0), 0);
    } catch { /* ignore */ }
  }
  return 0;
}

function formatDuration(ms: number): string {
  if (!ms || !Number.isFinite(ms)) return "-";
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatTotalDuration(totalMs: number): string {
  if (!totalMs) return "0m";
  const totalMinutes = Math.floor(totalMs / 60000);
  if (totalMinutes < 60) return `${totalMinutes}m`;
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  return `${hours}h ${mins}m`;
}

interface AdminLibraryViewProps {
  token: string | null;
  isAdmin: boolean;
}

export default function AdminLibraryView({ token, isAdmin }: AdminLibraryViewProps) {
  const navigate = useNavigate();
  const [items, setItems] = useState<LibraryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState("");
  const [toastMessage, setToastMessage] = useState("");
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [batchAdding, setBatchAdding] = useState(false);

  // Fetch library items
  useEffect(() => {
    if (!isAdmin) return;
    setLoading(true);
    fetch(`${API_BASE}/admin/library?limit=500`, {
      headers: {
        "X-Admin-Key": ADMIN_KEY,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data: any) => {
        const raw: any[] = Array.isArray(data) ? data : data.transcripts || data.items || [];
        const list = raw.map((item: any) => {
          const rawId = item.id ?? item.item_id ?? "";
          return {
            ...item,
            id: typeof rawId === "string" ? rawId : String(rawId),
          } as LibraryItem;
        });
        setItems(list);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, [isAdmin, token]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Toast auto-dismiss
  useEffect(() => {
    if (!toastMessage) return;
    const t = setTimeout(() => setToastMessage(""), 3000);
    return () => clearTimeout(t);
  }, [toastMessage]);

  const handleDelete = useCallback(async (id: string) => {
    if (!window.confirm("Delete this transcript?")) return;
    setDeletingId(id);
    setDeleteError("");
    try {
      const res = await fetch(`${API_BASE}/admin/library/${id}`, {
        method: "DELETE",
        headers: {
          "X-Admin-Key": ADMIN_KEY,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : "Delete failed");
    } finally {
      setDeletingId(null);
    }
  }, [token]);

  const handleAddToLibrary = useCallback(async (item: LibraryItem) => {
    const itemId = item.id || item.video_id;
    if (!itemId) {
      setToastMessage("Cannot copy: item has no id");
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/admin/library/${itemId}/copy`, {
        method: "POST",
        headers: { "X-Admin-Key": ADMIN_KEY, Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP ${res.status}`);
      }
      setToastMessage(`Added "${item.title}" to your library`);
    } catch (err) {
      setToastMessage(`Failed: ${err instanceof Error ? err.message : "Unknown error"}`);
    }
  }, [token]);

  const handleBatchAdd = useCallback(async () => {
    if (selectedIds.size === 0) return;
    setBatchAdding(true);
    let success = 0;
    let failed = 0;
    for (const id of selectedIds) {
      try {
        const res = await fetch(`${API_BASE}/admin/library/${id}/copy`, {
          method: "POST",
          headers: { "X-Admin-Key": ADMIN_KEY, Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error();
        success++;
      } catch {
        failed++;
      }
    }
    setBatchAdding(false);
    const msg = failed
      ? `Added ${success} item${success !== 1 ? "s" : ""}. ${failed} failed.`
      : `Added ${success} item${success !== 1 ? "s" : ""} to your library`;
    setToastMessage(msg);
    setSelectMode(false);
    setSelectedIds(new Set());
  }, [selectedIds, token]);

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    if (selectedIds.size === filteredItems.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredItems.map((i) => i.id)));
    }
  }

  function exitSelectMode() {
    setSelectMode(false);
    setSelectedIds(new Set());
  }

  const filteredItems = items.filter((item) => {
    if (!debouncedQuery) return true;
    const q = debouncedQuery.toLowerCase();
    return (
      item.title.toLowerCase().includes(q) ||
      (item.channel_name?.toLowerCase().includes(q) ?? false) ||
      (item.user_email?.toLowerCase().includes(q) ?? false)
    );
  });

  const totalDurationMs = items.reduce((acc, item) => acc + computeDurationMs(item), 0);
  const uniqueUsers = new Set(items.map((i) => i.user_email || i.user_id).filter(Boolean)).size;

  return (
    <>
      {/* Header */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold text-gray-900">
          Admin Library
        </h1>
        <div className="relative flex-1 max-w-xs">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
          >
            <path
              fillRule="evenodd"
              d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z"
              clipRule="evenodd"
            />
          </svg>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by title, channel, or user..."
            className="w-full rounded-md border border-gray-300 py-1.5 pl-8 pr-3 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <div className="ml-auto flex items-center gap-2">
          {selectMode && selectedIds.size > 0 && (
            <button
              onClick={handleBatchAdd}
              disabled={batchAdding}
              className="flex items-center gap-2 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {batchAdding ? (
                <>
                  <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                  Adding...
                </>
              ) : (
                `Add to my library (${selectedIds.size})`
              )}
            </button>
          )}
          {selectMode ? (
            <button
              onClick={exitSelectMode}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50"
            >
              Cancel
            </button>
          ) : (
            <button
              onClick={() => setSelectMode(true)}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50"
            >
              Select
            </button>
          )}
        </div>
      </div>

      {/* Select-all bar */}
      {selectMode && filteredItems.length > 0 && (
        <div className="mb-3 flex items-center gap-3">
          <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={selectedIds.size === filteredItems.length && filteredItems.length > 0}
              onChange={toggleSelectAll}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            Select all ({filteredItems.length})
          </label>
          {selectedIds.size > 0 && (
            <span className="text-sm text-blue-600 font-medium">
              {selectedIds.size} selected
            </span>
          )}
        </div>
      )}

      {/* Stats row */}
      <div className="mb-4 flex flex-wrap gap-3">
        <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
          {items.length} transcripts
        </span>
        <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
          {formatTotalDuration(totalDurationMs)} total duration
        </span>
        <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
          {uniqueUsers} unique user{uniqueUsers !== 1 ? "s" : ""}
        </span>
        {debouncedQuery && (
          <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-600">
            {filteredItems.length} result{filteredItems.length !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-600">
          {error}
        </div>
      )}

      {deleteError && (
        <div className="mb-4 flex items-center justify-between rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-600">
          <span>Delete failed: {deleteError}</span>
          <button onClick={() => setDeleteError("")} className="ml-3 font-medium hover:text-red-800">
            Dismiss
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" />
        </div>
      ) : filteredItems.length === 0 ? (
        <p className="py-20 text-center text-sm text-gray-400">
          {debouncedQuery ? "No matching transcripts." : "No transcripts yet."}
        </p>
      ) : (
        <div className="space-y-2">
          {filteredItems.map((item, idx) => {
            const durationMs = computeDurationMs(item);
            const wordCount = computeWordCount(item);
            return (
              <div
                key={item.id || idx}
                className={`group flex items-center justify-between rounded-lg border bg-white px-3 py-2 transition hover:shadow-sm ${
                  selectMode && selectedIds.has(item.id) ? "ring-2 ring-blue-500 border-blue-500" : ""
                }`}
                onClick={selectMode ? () => toggleSelect(item.id) : undefined}
              >
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  {selectMode && (
                    <input
                      type="checkbox"
                      checked={selectedIds.has(item.id)}
                      onChange={() => toggleSelect(item.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  )}
                  <Thumbnail
                    src={getItemThumbnailUrl(item as any)}
                    fallbackSrc={sanitizeThumbnailUrl((item as any).channel_artwork_url)}
                    alt={item.title ?? ""}
                    className={`shrink-0 rounded-md object-cover ${
                      item.source === "rss" ? "h-[68px] w-[68px]" : "h-[68px] w-[120px]"
                    }`}
                  />
                  <button
                    onClick={() => {
                      const navId = item.id || item.video_id;
                      if (!navId) return;
                      navigate(`/transcript/${navId}`);
                    }}
                    className="min-w-0 flex-1 text-left"
                  >
                    <p className="truncate text-sm font-medium text-gray-900">
                      {item.title}
                    </p>
                    <p className="mt-0.5 text-xs text-gray-500">
                      {item.channel_name && (
                        <span className="font-medium text-gray-600">{item.channel_name} &middot; </span>
                      )}
                      {formatDate(item.created_at)} &middot;{" "}
                      {formatDuration(durationMs)}
                      {wordCount > 0 && (
                        <> &middot; {wordCount.toLocaleString()} words</>
                      )}
                    </p>
                    {item.user_email && (
                      <p className="mt-0.5 text-xs text-gray-400">
                        {item.user_email}
                      </p>
                    )}
                  </button>
                </div>
                <div className={`ml-4 items-center gap-2 ${selectMode ? "hidden" : "hidden group-hover:flex"}`}>
                  <button
                    onClick={() => handleAddToLibrary(item)}
                    className="text-xs text-gray-400 hover:text-blue-600"
                    aria-label={`Add ${item.title} to library`}
                  >
                    Add to library
                  </button>
                  <span className="text-gray-200">|</span>
                  <button
                    onClick={() => handleDelete(item.id)}
                    disabled={deletingId === item.id}
                    className="text-xs text-gray-400 hover:text-red-500"
                    aria-label={`Delete ${item.title}`}
                  >
                    {deletingId === item.id ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-red-500" />
                    ) : (
                      "Delete"
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Toast */}
      {toastMessage && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-gray-900 px-4 py-2.5 text-sm text-white shadow-lg">
          {toastMessage}
        </div>
      )}
    </>
  );
}
